import { useState, useCallback, useRef } from 'react';
import { redactEvent, type RedactedActionEntry } from '@/lib/actionLogRedact';
import {
  buildSnapshot,
  type Snapshot,
  type SnapshotOptions,
  DEFAULT_WINDOW_MS,
} from '@/lib/actionLogSnapshot';
import { isActionKind } from '@/lib/actionLogKinds';

export interface ActionEntry {
  id: number;
  /** Legacy human-readable action label. */
  action: string;
  /** Legacy free-form detail (visas i aktivitets-rutan). */
  detail?: string;
  timestamp: number;
  /** Typad action-kind — PII-redacted payload. Null vid legacy-calls. */
  redacted?: RedactedActionEntry;
}

/** Max entries i ring-buffer. Bumped från 50 → 500 för att stödja tail-snapshot. */
const MAX_ENTRIES = 500;
/** Entries äldre än detta slängs vid nästa push (lätt-städning). */
const HARD_TTL_MS = 10 * 60 * 1000; // 10 min

export interface LogActionInput {
  /** Legacy: mänsklig label. */
  action: string;
  /** Legacy: fri text. */
  detail?: string;
  /** Optional: typad kind + redactable payload. */
  kind?: string;
  target?: string;
  payload?: Record<string, unknown>;
}

/**
 * Tracks recent user actions for an activity log.
 *
 * Behavior:
 *   - In-memory ring buffer, max 500 entries (~40 KB RAM).
 *   - Legacy `logAction(action, detail?)` still works — existing call
 *     sites don't need to change.
 *   - New form `logAction({action, kind, payload})` adds typed +
 *     PII-redacted entries usable in bug-report snapshot.
 *   - `snapshot()` serializes the TTL-windowed tail for attach to reports.
 */
export function useActionLog() {
  const [entries, setEntries] = useState<ActionEntry[]>([]);
  const nextId = useRef(1);

  const logAction = useCallback(
    (actionOrInput: string | LogActionInput, detailMaybe?: string) => {
      const now = Date.now();

      let input: LogActionInput;
      if (typeof actionOrInput === 'string') {
        input = { action: actionOrInput, detail: detailMaybe };
      } else {
        input = actionOrInput;
      }

      const entry: ActionEntry = {
        id: nextId.current++,
        action: input.action,
        detail: input.detail,
        timestamp: now,
      };

      if (input.kind && isActionKind(input.kind)) {
        const redacted = redactEvent({
          kind: input.kind,
          target: input.target,
          payload: input.payload,
        });
        if (redacted) entry.redacted = redacted;
      }

      setEntries(prev => {
        // Drop entries older than HARD_TTL, cap to MAX_ENTRIES.
        const pruned = prev.filter(e => now - e.timestamp <= HARD_TTL_MS);
        return [entry, ...pruned].slice(0, MAX_ENTRIES);
      });
    },
    [],
  );

  const clearLog = useCallback(() => {
    setEntries([]);
  }, []);

  /**
   * Serialize the typed-entry tail within `windowMs` into a Snapshot
   * suitable for attaching to a bug/feature report.
   */
  const snapshot = useCallback(
    (opts?: SnapshotOptions): Snapshot => {
      const timed = entries
        .filter(e => e.redacted !== undefined)
        .map(e => ({ entry: e.redacted!, timestamp: e.timestamp }));
      return buildSnapshot(timed, opts);
    },
    [entries],
  );

  return {
    entries,
    logAction,
    clearLog,
    snapshot,
    DEFAULT_WINDOW_MS,
  };
}
